'use client'

import { useEffect, useRef, useState, useCallback } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import type { GoogleCredentialResponse, GoogleOneTapUser, GoogleOneTapLoginData } from '@/types/auth';

interface GoogleOneTapProps {
  clientId: string;
  lang?: string;
  autoPrompt?: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Componente de Google One Tap Sign-In
 * Maneja automáticamente el flujo de login y redirección al registro si es necesario
 */
export default function GoogleOneTap({
  clientId,
  lang = 'es',
  autoPrompt = true,
  onSuccess,
  onError
}: GoogleOneTapProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { loginWithGoogleOneTap, sendGoogleSession, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const oneTapInitialized = useRef(false);

  /**
   * Carga el script de Google Identity Services
   */
  const loadGoogleScript = useCallback(() => {
    if (typeof window !== 'undefined' && !document.querySelector('script[src*="accounts.google.com/gsi"]')) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setScriptLoaded(true);
      };
      script.onerror = () => {
        console.error('Error loading Google Identity Services script');
        onError?.('Error loading Google Identity Services');
      };
      document.head.appendChild(script);
    } else if (typeof window !== 'undefined' && window.google?.accounts?.id) {
      setScriptLoaded(true);
    }
  }, [onError]);

  /**
   * Decodifica el JWT token de Google para extraer la información del usuario
   */
  const parseJWT = (token: string): GoogleOneTapUser | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      return JSON.parse(jsonPayload) as GoogleOneTapUser;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  /**
   * Maneja la respuesta de Google One Tap
   * Estrategia: Intentar login con email, si falla ir a registro
   */
  const handleCredentialResponse = useCallback(async (response: GoogleCredentialResponse) => {
    try {
      const userInfo = parseJWT(response.credential);

      if (!userInfo) {
        throw new Error('No se pudo obtener información del usuario');
      }

      // Estrategia: Usar el login normal con email pero sin password
      // Esto nos permitirá saber si el usuario existe sin problemas de validación de token
      const oneTapData: GoogleOneTapLoginData = {
        token: response.credential,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
        email_verified: userInfo.email_verified
      };

      const result = await loginWithGoogleOneTap(oneTapData);

      if (result.success) {
        toast({
          title: "¡Bienvenido!",
          description: "Has iniciado sesión correctamente",
        });
        onSuccess?.();
        // Redirigir al dashboard
        window.location.href = `/${lang}/dashboard/account`;
      } else if (result.needsRegistration && result.userEmail) {
        // Usuario no existe o hubo un error, redirigir al registro
        toast({
          title: "Crear cuenta",
          description: "Te redirigimos al registro para crear tu cuenta",
        });
        router.push(`/${lang}/signup?email=${encodeURIComponent(result.userEmail)}&fromOneTap=true&picture=${encodeURIComponent(oneTapData.picture)}`);
      } else {
        // Error general, también ir al registro como fallback
        toast({
          title: "Completar registro",
          description: "Te redirigimos para completar tu perfil",
        });
        router.push(`/${lang}/signup?email=${encodeURIComponent(userInfo.email)}&fromOneTap=true&picture=${encodeURIComponent(oneTapData.picture)}`);
      }
    } catch (error) {
      console.error('Error handling Google One Tap response:', error);

      // Si hay error, también intentamos ir al registro
      const userInfo = parseJWT(response.credential);
      if (userInfo?.email) {
        toast({
          title: "Creemos tu cuenta",
          description: "Te redirigimos al registro para crear tu cuenta",
        });
        router.push(`/${lang}/signup?email=${encodeURIComponent(userInfo.email)}&fromOneTap=true&picture=${encodeURIComponent(userInfo.picture)}`);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido en la autenticación';
        toast({
          title: "Error de autenticación",
          description: errorMessage,
          variant: "destructive",
        });
        onError?.(errorMessage);
      }
    }
  }, [loginWithGoogleOneTap, lang, onSuccess, onError, router, toast]);

  /**
   * Inicializa Google One Tap
   */
  const initializeGoogleOneTap = useCallback(() => {
    if (!scriptLoaded || !window.google?.accounts?.id || oneTapInitialized.current) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        use_fedcm_for_prompt: true
      });

      oneTapInitialized.current = true;
      setInitialized(true);

      if (autoPrompt) {
        // Pequeño delay para asegurar que la página se ha cargado completamente
        setTimeout(() => {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
            } else if (notification.isSkippedMoment()) {
              // Marcar como cerrado para evitar que reaparezca inmediatamente
              setIsDismissed(true);
            } else if (notification.isDismissedMoment()) {
              const reason = notification.getDismissedReason();

              // Si el usuario cancela o hace tap fuera, marcar como cerrado
              if (reason === 'user_cancel' || reason === 'tap_outside') {
                setIsDismissed(true);
              }
            }
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Error initializing Google One Tap:', error);
      onError?.('Error initializing Google One Tap');
    }
  }, [scriptLoaded, clientId, handleCredentialResponse, autoPrompt, onError]);

  /**
   * Cancela el prompt de Google One Tap si es necesario
   */
  const cancelOneTap = useCallback(() => {
    if (window.google?.accounts?.id && initialized) {
      window.google.accounts.id.cancel();
    }
  }, [initialized]);

  // Effect para cargar el script
  useEffect(() => {
    loadGoogleScript();
  }, [loadGoogleScript]);

  // Effect para inicializar cuando el script esté cargado
  useEffect(() => {
    if (scriptLoaded && !initialized) {
      initializeGoogleOneTap();
    }
  }, [scriptLoaded, initialized, initializeGoogleOneTap]);

  // Effect para resetear isDismissed cuando cambie la URL/página
  useEffect(() => {
    setIsDismissed(false);
  }, [pathname]);

  // Effect para cleanup
  useEffect(() => {
    return () => {
      cancelOneTap();
    };
  }, [cancelOneTap]);

  // No mostrar One Tap si el usuario ya está autenticado
  if (isAuthenticated) {
    return null;
  }

  // No mostrar One Tap si ha sido descartado por el usuario
  if (isDismissed) {
    return null;
  }

  // No mostrar One Tap en páginas de registro o si viene con parámetros específicos
  const isSignupPage = pathname.includes('/signup');
  const emailParam = searchParams.get('email');

  if (isSignupPage || emailParam) {
    return null;
  }

  // Este componente no renderiza nada visualmente, solo maneja la lógica
  return null;
}

/**
 * Hook personalizado para usar Google One Tap en cualquier componente
 */
export function useGoogleOneTap(clientId: string, options?: {
  lang?: string;
  autoPrompt?: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const [OneTapComponent, setOneTapComponent] = useState<React.ReactNode>(null);

  useEffect(() => {
    setOneTapComponent(
      <GoogleOneTap
        clientId={clientId}
        lang={options?.lang}
        autoPrompt={options?.autoPrompt}
        onSuccess={options?.onSuccess}
        onError={options?.onError}
      />
    );
  }, [clientId, options]);

  return OneTapComponent;
}
